import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';
import './Input.css';

/**
 * Select option
 */
interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

/**
 * Select component props
 */
export interface SelectProps<V extends string = string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  /**
   * Current value ('' for the placeholder option)
   */
  value: V | '';
  /**
   * Called with the selected value
   */
  onChange: (value: V | '') => void;
  /**
   * Options to render (children can be used instead)
   */
  options?: ReadonlyArray<SelectOption<V>>;
  /**
   * Text of a leading empty option (value '')
   */
  placeholder?: string;
  /**
   * Label text; when set, the select is wrapped with label/error/helper text
   */
  label?: string;
  error?: string;
  helperText?: string;
  /**
   * Extra class for the wrapper (only rendered with label/error/helperText)
   */
  wrapperClassName?: string;
  children?: ReactNode;
}

/**
 * Select primitive with a typed value and optional label/error.
 *
 * Without label/error/helperText it renders a bare `<select>` so it can sit
 * in filter bars; page CSS classes are applied through `className`.
 *
 * @example
 * ```tsx
 * <Select
 *   value={status}
 *   onChange={setStatus}
 *   placeholder="All Status"
 *   options={certificateStatusMap.options}
 *   className="certs-filter-select"
 * />
 * ```
 */
function SelectInner<V extends string = string>(
  {
    value,
    onChange,
    options,
    placeholder,
    label,
    error,
    helperText,
    wrapperClassName = '',
    required,
    id,
    className = '',
    children,
    ...props
  }: SelectProps<V>,
  ref: React.ForwardedRef<HTMLSelectElement>
) {
  const generatedId = useId();
  const selectId = id || generatedId;
  const errorId = `${selectId}-error`;
  const helperId = `${selectId}-helper`;

  const select = (
    <select
      ref={ref}
      id={selectId}
      value={value}
      onChange={(event) => onChange(event.target.value as V | '')}
      className={['select', className].filter(Boolean).join(' ')}
      required={required}
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={error ? errorId : helperText ? helperId : undefined}
      {...props}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options?.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
      {children}
    </select>
  );

  if (!label && !error && !helperText) {
    return select;
  }

  return (
    <div className={['input-wrapper', wrapperClassName].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={selectId} className="input-label">
          {label}
          {required && <span className="input-required">*</span>}
        </label>
      )}
      {select}
      {error && (
        <span id={errorId} className="input-error-message" role="alert">
          {error}
        </span>
      )}
      {!error && helperText && (
        <span id={helperId} className="input-helper-text">
          {helperText}
        </span>
      )}
    </div>
  );
}

export const Select = forwardRef(SelectInner) as <V extends string = string>(
  props: SelectProps<V> & { ref?: React.ForwardedRef<HTMLSelectElement> }
) => ReturnType<typeof SelectInner>;
